import { IconSearch } from '@myplayer/ui';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type ChangeEvent,
  type ReactElement,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/utils.js';
import { Button, Tag } from './primitives.js';
import {
  parseSelectOptionChildren,
  useDropdownPopoverPosition,
  useExclusiveDropdownClose,
} from './searchable-multi-select.js';

export { useDropdownPopoverPosition, useExclusiveDropdownClose };

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  readonly filterLabel?: string;
  readonly defaultFilterValue?: string;
  readonly searchable?: boolean;
  readonly searchPlaceholder?: string;
  readonly optionCounts?: Readonly<Record<string, number>> | undefined;
  readonly leadingIcon?: ReactNode | undefined;
}

/**
 * Custom MyPlayer popover dropdown that replaces native `<select>` appearance
 * across the entire Admin Console while keeping a visually-hidden `<select>`
 * in sync for full screen-reader and Vitest `userEvent.selectOptions` support.
 */
export function Select({
  className,
  children,
  value,
  defaultValue,
  disabled,
  onChange,
  filterLabel,
  defaultFilterValue = 'ALL',
  searchable = true,
  searchPlaceholder,
  optionCounts,
  leadingIcon,
  ...props
}: SelectProps): ReactElement {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const selectRef = useRef<HTMLSelectElement | null>(null);
  const popoverStyle = useDropdownPopoverPosition(open, triggerRef);

  useExclusiveDropdownClose(open, () => {
    setOpen(false);
    setSearchQuery('');
  });

  const options = useMemo(() => parseSelectOptionChildren(children), [children]);
  const currentValue =
    value !== undefined
      ? String(value)
      : defaultValue !== undefined
        ? String(defaultValue)
        : (options[0]?.value ?? '');

  const isFilterMode = filterLabel !== undefined;
  const effectiveSearchPlaceholder =
    searchPlaceholder ?? (filterLabel ? `Search ${filterLabel.toLowerCase()}...` : 'Search...');

  const selectedValues = useMemo<readonly string[]>(() => {
    if (!isFilterMode) return currentValue ? [currentValue] : [];
    if (!currentValue || currentValue === defaultFilterValue) return [];
    return currentValue
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0 && s !== defaultFilterValue);
  }, [isFilterMode, currentValue, defaultFilterValue]);

  const selectedOption = options.find((opt) => opt.value === currentValue) ??
    options[0] ?? { value: currentValue, label: currentValue, disabled: false };

  const isFilteredActive = isFilterMode && selectedValues.length > 0;

  const filterableOptions = useMemo(
    () => (isFilterMode ? options.filter((opt) => opt.value !== defaultFilterValue) : options),
    [isFilterMode, options, defaultFilterValue],
  );

  const filteredOptions = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (q.length === 0) return filterableOptions;
    return filterableOptions.filter(
      (opt) => opt.label.toLowerCase().includes(q) || opt.value.toLowerCase().includes(q),
    );
  }, [filterableOptions, searchQuery]);

  useEffect(() => {
    if (!open || typeof document === 'undefined') return;
    const handleMouseDown = (event: MouseEvent): void => {
      if (
        event.target instanceof Node &&
        !containerRef.current?.contains(event.target) &&
        !popoverRef.current?.contains(event.target)
      ) {
        setOpen(false);
        setSearchQuery('');
      }
    };
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        setOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handleMouseDown, true);
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown, true);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [open]);

  const emitChange = (nextValue: string): void => {
    if (!onChange) return;
    if (selectRef.current && !isFilterMode) {
      try {
        selectRef.current.value = nextValue;
      } catch {
        // Ignore read-only assignment on synthetic fallback
      }
    }
    const syntheticEvent = {
      target: { value: nextValue },
      currentTarget: { value: nextValue },
      preventDefault: () => undefined,
      stopPropagation: () => undefined,
    } as unknown as ChangeEvent<HTMLSelectElement>;
    onChange(syntheticEvent);
  };

  const handleSelectOption = (nextValue: string): void => {
    if (isFilterMode) {
      if (nextValue === defaultFilterValue) {
        emitChange(defaultFilterValue);
        return;
      }
      const exists = selectedValues.includes(nextValue);
      const nextList = exists
        ? selectedValues.filter((v) => v !== nextValue)
        : [...selectedValues, nextValue];
      const nextJoined = nextList.length === 0 ? defaultFilterValue : nextList.join(',');
      emitChange(nextJoined);
      return;
    }
    setOpen(false);
    setSearchQuery('');
    emitChange(nextValue);
  };

  const activeFilterSummaryLabel = useMemo(() => {
    if (!isFilteredActive) return '';
    if (selectedValues.length === 1) {
      const firstVal = selectedValues[0] ?? '';
      return options.find((o) => o.value === firstVal)?.label ?? firstVal;
    }
    return `${String(selectedValues.length)} selected`;
  }, [isFilteredActive, selectedValues, options]);

  const selectNativeValue =
    isFilterMode && selectedValues.length > 1 ? (selectedValues[0] ?? defaultFilterValue) : value;

  return (
    <div ref={containerRef} className="relative min-w-0">
      <select
        ref={selectRef}
        tabIndex={-1}
        value={selectNativeValue}
        defaultValue={defaultValue}
        disabled={disabled}
        onChange={onChange}
        className="sr-only"
        {...props}
      >
        {children}
      </select>

      <Button
        ref={triggerRef}
        type="button"
        variant="ghost"
        size="inline"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => {
          if (!disabled) {
            setOpen((prev) => !prev);
            setSearchQuery('');
          }
        }}
        className={cn(
          'box-border flex h-[36px] min-h-[36px] max-h-[36px] w-full max-w-full min-w-0 cursor-pointer items-center justify-between gap-2 rounded-control border px-3 py-0 text-left text-[13px] font-medium leading-none transition-colors focus-visible:border-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-wash disabled:cursor-not-allowed disabled:opacity-50',
          open
            ? 'border-brand bg-surface text-fg shadow-card-base'
            : isFilteredActive
              ? 'border-brand-border-tint bg-brand-wash text-fg'
              : 'border-border bg-surface-subtle text-fg hover:border-border-medium hover:bg-surface',
          className,
        )}
      >
        <span className="flex min-w-0 flex-1 items-center gap-1.5 truncate">
          {leadingIcon !== undefined && (
            <span className="shrink-0 text-fg-secondary">{leadingIcon}</span>
          )}
          {isFilterMode ? (
            <>
              <span className="shrink-0 text-[13px] font-medium text-fg">{filterLabel}</span>
              {isFilteredActive && (
                <Tag variant="accent" size="sm" className="max-w-full truncate">
                  {activeFilterSummaryLabel}
                </Tag>
              )}
            </>
          ) : (
            <span className="truncate">{selectedOption.label}</span>
          )}
        </span>
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className={cn(
            'shrink-0 text-fg-secondary transition-transform duration-150',
            open && 'rotate-180 text-brand',
          )}
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </Button>

      {open &&
        (() => {
          const popoverEl = (
            <div
              ref={popoverRef}
              role="listbox"
              aria-multiselectable={isFilterMode ? 'true' : undefined}
              style={popoverStyle}
              className={cn(
                'w-max min-w-[230px] overflow-hidden rounded-card border border-border bg-surface shadow-modal',
                popoverStyle.position === 'fixed'
                  ? ''
                  : 'absolute left-0 top-full z-50 mt-1.5 min-w-[230px] max-w-[min(380px,calc(100vw-2.5rem))]',
              )}
            >
              {isFilterMode && (
                <div className="flex items-center justify-between border-b border-subtle bg-surface px-3.5 py-2.5">
                  <span className="text-[13px] font-semibold text-fg">{filterLabel}</span>
                  {isFilteredActive && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="inline"
                      onClick={() => handleSelectOption(defaultFilterValue)}
                      className="text-[11px] font-semibold text-brand hover:underline"
                    >
                      Clear
                    </Button>
                  )}
                </div>
              )}

              {searchable && (isFilterMode || options.length > 6) && (
                <div className="border-b border-subtle p-2.5">
                  <div className="relative">
                    <IconSearch className="pointer-events-none absolute left-2.5 top-2 h-4 w-4 text-fg-muted" />
                    <input
                      type="search"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={effectiveSearchPlaceholder}
                      aria-label={effectiveSearchPlaceholder}
                      className="h-8 w-full rounded-control border border-border bg-surface-subtle pl-8 pr-2.5 text-[12px] text-fg placeholder:text-fg-muted focus:border-brand focus:bg-surface focus:outline-none"
                    />
                  </div>
                </div>
              )}

              <div className="max-h-64 overflow-y-auto p-1.5">
                {filteredOptions.length === 0 ? (
                  <div className="px-3 py-2 text-[12px] text-fg-muted">No matching options</div>
                ) : (
                  filteredOptions.map((opt) => {
                    const isSelected = isFilterMode
                      ? selectedValues.includes(opt.value)
                      : opt.value === currentValue;
                    const count = optionCounts?.[opt.value];
                    return (
                      <Button
                        key={opt.value}
                        type="button"
                        variant="ghost"
                        size="inline"
                        role="option"
                        aria-selected={isSelected}
                        disabled={opt.disabled}
                        onClick={() => handleSelectOption(opt.value)}
                        className={cn(
                          'flex min-h-[34px] w-full cursor-pointer items-center justify-between gap-3 rounded-[5px] px-3 py-2 text-left text-[13px] transition-colors disabled:cursor-not-allowed disabled:opacity-45',
                          isSelected
                            ? 'bg-brand-wash font-semibold text-fg'
                            : 'font-medium text-fg hover:bg-surface-subtle',
                        )}
                      >
                        <span className="flex min-w-0 flex-1 items-center gap-2.5 truncate">
                          {isFilterMode && (
                            <span
                              aria-hidden="true"
                              className={cn(
                                'flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px] border transition-colors',
                                isSelected
                                  ? 'border-brand bg-brand text-brand-fg'
                                  : 'border-border bg-surface-subtle',
                              )}
                            >
                              {isSelected && (
                                <svg
                                  width="11"
                                  height="11"
                                  viewBox="0 0 12 12"
                                  fill="none"
                                  aria-hidden="true"
                                >
                                  <path
                                    d="M2.25 6.25L4.75 8.75L9.75 3.25"
                                    stroke="currentColor"
                                    strokeWidth="2.2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              )}
                            </span>
                          )}
                          <span className="truncate">{opt.label}</span>
                        </span>
                        {isFilterMode
                          ? count !== undefined && (
                              <span
                                data-testid={`filter-option-count-${opt.value}`}
                                className="shrink-0 text-[12px] font-medium text-fg-secondary tabular-nums"
                              >
                                {String(count)}
                              </span>
                            )
                          : isSelected && (
                              <svg
                                width="15"
                                height="15"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden="true"
                                className="shrink-0 text-brand"
                              >
                                <path d="M20 6 9 17l-5-5" />
                              </svg>
                            )}
                      </Button>
                    );
                  })
                )}
              </div>
            </div>
          );
          return popoverStyle.position === 'fixed' && typeof document !== 'undefined'
            ? createPortal(popoverEl, document.body)
            : popoverEl;
        })()}
    </div>
  );
}

export interface FilterClearButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly active?: boolean;
}

export function FilterClearButton({
  active = true,
  disabled,
  className,
  children = 'Clear',
  ...props
}: FilterClearButtonProps): ReactElement {
  return (
    <Button
      type="button"
      variant="secondary"
      size="default"
      disabled={disabled ?? !active}
      className={cn('shrink-0', className)}
      {...props}
    >
      {children}
    </Button>
  );
}
